import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Row,
  Col,
  Avatar,
  Typography,
  Divider,
  Tabs,
  Space,
  Tag,
  Modal,
  Upload,
  Spin,
} from 'antd';
import {
  UserOutlined,
  EditOutlined,
  SaveOutlined,
  CameraOutlined,
  LockOutlined,
  PhoneOutlined,
  MailOutlined,
  IdcardOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  UploadOutlined,
} from '@ant-design/icons';
import { ProfileService, UpdateProfileData } from '../services/profileService';
import { AuthService } from '../services/authService';
import type { UploadFile } from 'antd';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface UserProfile {
  userId: number;
  username: string;
  fullName: string;
  email: string;
  phone: string;
  address: string;
  avatar: string;
  roleName: string;
  createdAt: string;
  lastLogin: string;
  isActive: boolean;
}

interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ProfilePage: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await ProfileService.getProfile();
      setProfile(data);
      form.setFieldsValue(data);
    } catch (error) {
      message.error('Không thể tải thông tin hồ sơ');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (values: Partial<UserProfile>) => {
    try {
      setLoading(true);
      
      // Validate and prepare update data
      const updateData: UpdateProfileData = {
        fullName: values.fullName || '',
        email: values.email || '',
        phone: values.phone,
        address: values.address
      };
      
      // Check required fields
      if (!updateData.fullName.trim()) {
        message.error('Họ tên không được để trống');
        return;
      }
      
      if (!updateData.email.trim()) {
        message.error('Email không được để trống');
        return;
      }
      
      const updatedProfile = await ProfileService.updateProfile(updateData);
      setProfile(updatedProfile);
      setEditing(false);
      message.success('Cập nhật thông tin thành công');
    } catch (error) {
      message.error('Cập nhật thông tin thất bại');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (values: ChangePasswordData) => {
    try {
      setLoading(true);
      await ProfileService.changePassword(values);
      setChangePasswordVisible(false);
      passwordForm.resetFields();
      message.success('Đổi mật khẩu thành công');
    } catch (error) {
      message.error('Đổi mật khẩu thất bại');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (file: File) => {
    try {
      setUploadLoading(true);
      const updatedProfile = await ProfileService.uploadAvatar(file);
      setProfile(updatedProfile);
      message.success('Cập nhật ảnh đại diện thành công');
    } catch (error) {
      message.error('Cập nhật ảnh đại diện thất bại');
    } finally {
      setUploadLoading(false);
    }
  };

  const beforeUpload = (file: File) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Chỉ được phép tải lên file hình ảnh!');
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Kích thước file phải nhỏ hơn 5MB!');
      return false;
    }
    
    handleAvatarUpload(file);
    return false; // Prevent default upload
  };

  if (loading && !profile) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const profileInfo = (
    <Card>
      <Row gutter={24}>
        <Col span={6}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <Avatar
              size={120}
              src={profile?.avatar}
              icon={<UserOutlined />}
              style={{ marginBottom: 16 }}
            />
            <div>
              <Upload
                showUploadList={false}
                beforeUpload={beforeUpload}
                accept="image/*"
              >
                <Button 
                  icon={<CameraOutlined />} 
                  loading={uploadLoading}
                  type="dashed"
                  size="small"
                >
                  Đổi ảnh đại diện
                </Button>
              </Upload>
            </div>
          </div>
        </Col>
        <Col span={18}>
          <div style={{ marginBottom: 16 }}>
            <Title level={3} style={{ marginBottom: 8 }}>
              {profile?.fullName}
            </Title>
            <Space>
              <Tag color="blue">{profile?.roleName}</Tag>
              <Tag color={profile?.isActive ? 'green' : 'red'}>
                {profile?.isActive ? 'Hoạt động' : 'Không hoạt động'}
              </Tag>
            </Space>
          </div>
          
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Space>
                <IdcardOutlined />
                <Text strong>Tên đăng nhập:</Text>
                <Text>{profile?.username}</Text>
              </Space>
            </Col>
            <Col span={12}>
              <Space>
                <MailOutlined />
                <Text strong>Email:</Text>
                <Text>{profile?.email}</Text>
              </Space>
            </Col>
            <Col span={12}>
              <Space>
                <PhoneOutlined />
                <Text strong>Số điện thoại:</Text>
                <Text>{profile?.phone || 'Chưa cập nhật'}</Text>
              </Space>
            </Col>
            <Col span={12}>
              <Space>
                <UserOutlined />
                <Text strong>Ngày tạo:</Text>
                <Text>{new Date(profile?.createdAt || '').toLocaleDateString('vi-VN')}</Text>
              </Space>
            </Col>
            <Col span={24}>
              <Space>
                <IdcardOutlined />
                <Text strong>Địa chỉ:</Text>
                <Text>{profile?.address || 'Chưa cập nhật'}</Text>
              </Space>
            </Col>
            <Col span={24}>
              <Space>
                <UserOutlined />
                <Text strong>Lần đăng nhập cuối:</Text>
                <Text>{profile?.lastLogin ? new Date(profile.lastLogin).toLocaleString('vi-VN') : 'Chưa rõ'}</Text>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  );

  const editForm = (
    <Card>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleUpdateProfile}
        initialValues={profile || {}}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label="Họ và tên"
              name="fullName"
              rules={[{ required: true, message: 'Vui lòng nhập họ và tên!' }]}
            >
              <Input prefix={<UserOutlined />} placeholder="Nhập họ và tên" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Vui lòng nhập email!' },
                { type: 'email', message: 'Email không hợp lệ!' }
              ]}
            >
              <Input prefix={<MailOutlined />} placeholder="Nhập email" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Số điện thoại"
              name="phone"
              rules={[
                { pattern: /^[0-9]{10,11}$/, message: 'Số điện thoại không hợp lệ!' }
              ]}
            >
              <Input prefix={<PhoneOutlined />} placeholder="Nhập số điện thoại" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Tên đăng nhập"
              name="username"
            >
              <Input prefix={<IdcardOutlined />} disabled />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Địa chỉ"
              name="address"
            >
              <Input.TextArea rows={3} placeholder="Nhập địa chỉ" />
            </Form.Item>
          </Col>
        </Row>
        
        <div style={{ textAlign: 'right', marginTop: 24 }}>
          <Space>
            <Button onClick={() => setEditing(false)}>
              Hủy
            </Button>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
              Lưu thay đổi
            </Button>
          </Space>
        </div>
      </Form>
    </Card>
  );

  const changePasswordModal = (
    <Modal
      title="Đổi mật khẩu"
      open={changePasswordVisible}
      onCancel={() => setChangePasswordVisible(false)}
      footer={null}
      width={400}
    >
      <Form
        form={passwordForm}
        layout="vertical"
        onFinish={handleChangePassword}
      >
        <Form.Item
          label="Mật khẩu hiện tại"
          name="currentPassword"
          rules={[{ required: true, message: 'Vui lòng nhập mật khẩu hiện tại!' }]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Nhập mật khẩu hiện tại"
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <Form.Item
          label="Mật khẩu mới"
          name="newPassword"
          rules={[
            { required: true, message: 'Vui lòng nhập mật khẩu mới!' },
            { min: 6, message: 'Mật khẩu phải có ít nhất 6 ký tự!' }
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Nhập mật khẩu mới"
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <Form.Item
          label="Xác nhận mật khẩu mới"
          name="confirmPassword"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Vui lòng xác nhận mật khẩu mới!' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Mật khẩu xác nhận không khớp!'));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Xác nhận mật khẩu mới"
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <div style={{ textAlign: 'right', marginTop: 24 }}>
          <Space>
            <Button onClick={() => setChangePasswordVisible(false)}>
              Hủy
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Đổi mật khẩu
            </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2}>Hồ sơ cá nhân</Title>
        <Space>
          <Button
            type="primary"
            icon={<LockOutlined />}
            onClick={() => setChangePasswordVisible(true)}
          >
            Đổi mật khẩu
          </Button>
          <Button
            type={editing ? 'default' : 'primary'}
            icon={<EditOutlined />}
            onClick={() => setEditing(!editing)}
          >
            {editing ? 'Hủy chỉnh sửa' : 'Chỉnh sửa'}
          </Button>
        </Space>
      </div>

      {editing ? editForm : profileInfo}
      {changePasswordModal}
    </div>
  );
};

export default ProfilePage;
